import _ from 'lodash'
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sortListWithEntityType } from '../../libraries/entityTools'
import SelectWithData from './SelectWithData'

//actions
import entityActions, { getFilterString } from '../../store/entity/actions'
import { sanitiseFilterString } from '../../libraries/sanitiseFilterString'
import { shouldRefetch } from './RadioList'
import PublicFormContext from '../../contexts/PublicFormContext'
import CrossFieldRenderAsNullContext from '../../contexts/CrossFieldRenderAsNullContext'
import { evaluateRule } from '../query/evaluateRule'
import { flattenEntity } from '../../helpers/entity'
import { listInstancesOfEventsUpcoming } from '../../store/calendar/actions'
import EntityEditContext from '../../contexts/EntityEditContext'
import { useDiscoverEntityEntityType } from '../../hooks/useDiscoverEntityEntityType'
import {
  getAvailableFilters,
  simplifyFilters
} from '../../screens/EntityListScreen'

const _SelectList = (props, ref) => {
  const dispatch = useDispatch()

  const { editable = true } = props
  const publicFormContext = useContext(PublicFormContext)

  const [, , , , , , , , , , , , filterRulesRequiredFromEffects] = useContext(
    EntityEditContext
  )

  const [nextPages, setNextPages] = useState({})

  const recurringEventDefinitions = useSelector((state) =>
    _.get(state, 'calendar.eventDefinitions.recurringEvents', [])
  )

  const sourceNames = useMemo(() => {
    return _.castArray(props.data.field.field_data.params.sourceName)
  }, [props.data.field.field_data.params.sourceName])

  const [loading, setLoading] = useState(false)
  const [loadingFuture, setLoadingFuture] = useState(false)

  const { entityType } = useDiscoverEntityEntityType(
    null,
    null,
    props.data.field.field_data.params.sourceName,
    null,
    false
  )
  const [serverFilter, setServerFilter] = useState(
    props.data?.field?.field_data?.params?.serverFilter
  )
  const [recurringEventEntities, setRecurringEventEntities] = useState([])
  const debounceTimeoutRef = useRef(null)

  useEffect(() => {
    const availableFilters = getAvailableFilters(entityType)
    const newServerFilter = entityType?.object_data?.filters_selected
      ? simplifyFilters(
          {},
          entityType?.object_data?.filters_selected,
          availableFilters
        )
      : props.data?.field?.field_data?.params?.serverFilter

    setServerFilter(newServerFilter)
  }, [entityType])

  const debouncedSetFilter = useCallback((searchText) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setServerFilter((_serverFilter) => {
        return {
          ..._serverFilter,
          display_name: searchText ?? undefined
        }
      })
      setNextPages({ 0: 1 })
    }, 300)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (props.data.field?.field_data?.params?.loadFutureEntities) {
      const matchingRecurringEvents = recurringEventDefinitions?.filter((re) =>
        sourceNames.includes(re.entityType?.name)
      )
      if (matchingRecurringEvents?.length && !recurringEventEntities.length) {
        setLoadingFuture(true)
        dispatch(
          listInstancesOfEventsUpcoming(
            matchingRecurringEvents.map((r) => r.id)
          )
        ).then((upcomingResponse) => {
          setLoadingFuture(false)
          setRecurringEventEntities(upcomingResponse.data)
        })
      }
    }
  }, ['', sourceNames, recurringEventDefinitions])

  const filterRule =
    props?.data?.field?.field_data?.params?.optionsFilter?.json?.logic ||
    (filterRulesRequiredFromEffects ?? []).find(
      (f) =>
        f.field.field_data.property === props?.data?.field?.field_data?.property
    )?.optionsFilter?.json?.logic

  const needsFieldsToSort = useMemo(() => {
    const sortField = _.get(
      entityType,
      'object_data.sort_by_field.field_data.property',
      null
    )

    return !!sortField
  }, [entityType])

  const filter =
    filterRule ||
    needsFieldsToSort ||
    props?.data?.field?.field_data?.params?.forceDetailed
      ? {
          detailed: true
        }
      : null

  const filterString = sanitiseFilterString(
    getFilterString(serverFilter, filter)
  )

  const sourceListRoots = sourceNames.map((entityTypeName) => {
    return useSelector((state) => {
      return entityTypeName
        ? _.get(state.entities, `[${entityTypeName + filterString}]`, {
            data: []
          })
        : []
    })
  })

  const fetchNextPage = (index, forcePage = null) => {
    if (_.get(nextPages, index, false) !== -1 || forcePage) {
      let pageToFetch

      if (forcePage !== null) {
        pageToFetch = forcePage
      } else {
        try {
          pageToFetch = sourceListRoots[index].meta.current_page + 1
        } catch (e) {
          // doesn't matter.
          pageToFetch = nextPages[index]
        }

        if (
          !pageToFetch ||
          pageToFetch > sourceListRoots[index].meta?.last_page
        ) {
          pageToFetch = 1
        }
      }

      setLoading((_loading) => (_loading ?? 0) + 1)
      dispatch(
        entityActions.listEntity(
          { name: sourceNames[index] },
          {
            filter: {
              ...filter,
              ...serverFilter
            },
            page: pageToFetch,
            detailed: filter?.detailed
          },
          undefined,
          publicFormContext.entityType,
          {
            tenantId: publicFormContext.tenantId,
            tenantHmac: publicFormContext.tenantHmac
          }
        )
      )
        .then((response) => {
          if (
            !nextPages[index] ||
            nextPages[index] <= response.meta.last_page
          ) {
            if (response.meta.current_page < response.meta.last_page) {
              setNextPages((nextPages) => ({
                ...nextPages,
                ...{ [index]: response.meta.current_page + 1 }
              }))
            } else {
              setNextPages((nextPages) => ({
                ...nextPages,
                ...{ [index]: -1 }
              }))
            }
          } else {
            setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: -1 } }))
          }
        })
        .finally(() => {
          setLoading((_loading) => (_loading > 0 ? _loading - 1 : 0))
        })
    }
  }

  useEffect(() => {
    sourceListRoots.map((sourceListRoot, index) => {
      if (shouldRefetch(sourceListRoot) && editable) {
        fetchNextPage(index)
      } else {
        setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: 1 } }))
      }
    })
  }, [editable])

  useEffect(() => {
    if (entityType && editable) {
      setNextPages({ 0: 1 })
      fetchNextPage(0, 1)
    }
  }, [serverFilter, entityType, editable])

  const sourceData = useMemo(() => {
    const data = _.flatten([
      ...sourceListRoots.map((sourceListRoot) => {
        const data = sortListWithEntityType(
          _.get(sourceListRoot, 'data', []),
          entityType
        )

        return data
      }),
      ...recurringEventEntities
    ])

    const filteredData = filterRule
      ? data.filter((d) =>
          evaluateRule(filterRule, {
            ...flattenEntity(d, false),
            field: flattenEntity(props.data.entity, false)
          })
        )
      : props.data?.field?.field_data?.params?.tenantFilter
      ? data.filter(
          (d) => d.tenant_id === props.data.field.field_data.params.tenantFilter
        )
      : data

    // const sorted = filteredData.sort((a, b) => {
    //   return a.sort_data?.localeCompare(b.sort_data)
    // })

    // return sorted
    return filteredData
  }, [sourceListRoots, filterRule, entityType, recurringEventEntities])

  const groupByFunction =
    sourceListRoots.length > 1
      ? (f) => {
          return f.entity_type.label
        }
      : undefined

  const { crossFieldRenderAsNull } = useContext(CrossFieldRenderAsNullContext)
  const skipStaticRendering =
    _.get(
      crossFieldRenderAsNull,
      `suppressObject[${props.data?.field?.field_data?.property}]`,
      true
    ) === false

  return skipStaticRendering ? null : (
    <SelectWithData
      {...props}
      dataRows={sourceData}
      groupByFunction={groupByFunction}
      labelField={'display_name'}
      onEndReached={() => {
        // console.log("ONENDREACHED")
        sourceListRoots.map((sourceListRoot, index) => {
          fetchNextPage(index)
        })
      }}
      onSearchTextChanged={(searchText) => {
        if (searchText.length == 0 || searchText.length > 2) {
          debouncedSetFilter(searchText)
        }
      }}
      loading={loadingFuture || loading}
      value={props.value}
    />
  )
}
export const SelectList = React.forwardRef(_SelectList)
